import { useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { PlayCircle, Heart, CheckCircle } from "@phosphor-icons/react";
import Container from "Components/Container/Container";
import { LoginAuthContext } from "Context/Authentication/LoginAuth";
import IsLoading from "Components/RequestHandler/IsLoading";
import Shelf from "Pages/Categories/Shelves/Shelf";
import ContinueLearningHero from "Pages/Categories/Shelves/ContinueLearningHero";
import BASE_URL from "Utilities/BASE_URL";

const MyCourses = () => {
  const { t, i18n } = useTranslation();
  const { token, selectedUser, isAuthenticated, allowedCourses } =
    useContext(LoginAuthContext);

  // Profile docs come back from different endpoints with either `_id` or `id`,
  // so accept whichever is populated.
  const profileId = selectedUser?._id || selectedUser?.id;

  // Subscriber data — Continue Watching + Completed
  const { data: progressData, isLoading: progressLoading } = useQuery({
    queryKey: ["my-progress", profileId, i18n.language],
    queryFn: async () => {
      const res = await fetch(`${BASE_URL}/my-progress`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ profile_id: profileId }),
      });
      if (!res.ok) throw new Error("Progress fetch failed");
      const json = await res.json();
      return json?.data || null;
    },
    enabled: !!(isAuthenticated && token && profileId),
    staleTime: 60 * 1000,
  });

  // My List
  const { data: myListData, isLoading: listLoading } = useQuery({
    queryKey: ["my-list", profileId, i18n.language],
    queryFn: async () => {
      const res = await fetch(
        `${BASE_URL}/my-list?profile_id=${profileId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error("My list fetch failed");
      return res.json();
    },
    enabled: !!(isAuthenticated && token && profileId),
    staleTime: 60 * 1000,
  });

  // Purchased courses (for non-subscribers who bought individual courses)
  const { data: purchasedData } = useQuery({
    queryKey: ["my-purchased-courses", i18n.language],
    queryFn: async () => {
      const res = await fetch(`${BASE_URL}/my-courses`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Purchased courses fetch failed");
      return res.json();
    },
    enabled: !!(token && !isAuthenticated && (allowedCourses?.length > 0)),
    staleTime: 5 * 60 * 1000,
  });

  const continueWatching = progressData?.continue_watching || [];
  const completedCourses = progressData?.completed_courses || [];
  const myList = Array.isArray(myListData?.data?.courses)
    ? myListData.data.courses
    : Array.isArray(myListData?.data)
    ? myListData.data
    : Array.isArray(myListData?.courses)
    ? myListData.courses
    : [];
  const purchasedCourses = Array.isArray(purchasedData?.data)
    ? purchasedData.data
    : [];

  // Progress map
  const progressMap = useMemo(() => {
    const map = {};
    continueWatching.forEach((item) => {
      const id = item?.course?._id;
      if (id) {
        map[id] = {
          percent: Math.min(100, Math.max(0, item?.progressPercent ?? 0)),
          lessonsCompleted: item?.lessonsCompleted ?? 0,
          totalLessons: item?.totalLessons ?? 0,
        };
      }
    });
    completedCourses.forEach((item) => {
      const id = item?._id || item?.course?._id;
      if (id) {
        map[id] = { ...(map[id] || {}), isCompleted: true, percent: 100 };
      }
    });
    return map;
  }, [continueWatching, completedCourses]);

  // Hero = most recent in-progress
  const heroItem = continueWatching.length > 0 ? continueWatching[0] : null;
  const heroCourseId = heroItem?.course?._id;
  const continueWatchingShelfItems = continueWatching.filter(
    (item) => item?.course?._id !== heroCourseId
  );

  if (isAuthenticated && (progressLoading || listLoading)) {
    return <IsLoading />;
  }

  const isEmpty =
    isAuthenticated &&
    continueWatching.length === 0 &&
    myList.length === 0 &&
    completedCourses.length === 0;

  return (
    <main className="pb-16 lg:pb-primary pt-pageTop lg:pt-primary min-h-screen">
      <Container>
        {/* Page title */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-white sm:text-4xl">
            {t("navigation.my_courses") || "My Courses"}
          </h1>
          {isAuthenticated && (
            <p className="text-white/60 text-sm mt-2">
              {t("my_courses.subtitle") ||
                "Everything you're learning, saved, and completed"}
            </p>
          )}
        </div>

        {/* Continue Learning Hero — subscribers only */}
        {isAuthenticated && heroItem && <ContinueLearningHero resumeItem={heroItem} />}

        {/* Subscriber shelves */}
        {isAuthenticated && (
          <>
            <Shelf
              title={t("library.continue_learning") || "Continue Learning"}
              icon={<PlayCircle size={20} className="text-primary" weight="fill" />}
              items={continueWatchingShelfItems}
              progressMap={progressMap}
            />
            <Shelf
              title={t("library.my_list") || "My List"}
              icon={<Heart size={20} className="text-primary" weight="fill" />}
              items={myList}
              progressMap={progressMap}
            />
            <Shelf
              title={t("library.completed") || "Completed"}
              icon={<CheckCircle size={20} className="text-green-400" weight="fill" />}
              items={completedCourses}
              progressMap={progressMap}
            />

            {isEmpty && (
              <div className="mt-16 text-center">
                <p className="text-white/60 text-sm">
                  {t("library.my_courses_empty") ||
                    "Start or save a course to see it here"}
                </p>
              </div>
            )}
          </>
        )}

        {/* Non-subscriber: just show purchased courses */}
        {!isAuthenticated && purchasedCourses.length > 0 && (
          <Shelf
            title={t("my_courses.purchased_courses") || "My Purchased Courses"}
            icon={<PlayCircle size={20} className="text-primary" weight="fill" />}
            items={purchasedCourses}
          />
        )}

        {!isAuthenticated && purchasedCourses.length === 0 && (
          <div className="mt-16 text-center">
            <p className="text-white/60 text-sm">
              {t("my_courses.no_courses") ||
                "You haven't purchased any courses yet."}
            </p>
          </div>
        )}
      </Container>
    </main>
  );
};

export default MyCourses;
